import type { AppSearchResult, AppDetermination } from "./types";

/**
 * Analyzes app search results and determines the best match
 * based on query similarity and recency
 */
export function determineAppMatch(
  query: string,
  results: AppSearchResult[],
): AppDetermination {
  if (results.length === 0) {
    throw new Error("No apps found for the given query");
  }

  // Simple scoring algorithm - prefer exact matches and newer versions
  const scored = results.map((app) => {
    let score = 0;
    const queryLower = query.toLowerCase();
    const nameLower = app.name.toLowerCase();

    // Exact match gets highest score
    if (nameLower === queryLower) {
      score += 100;
    }
    // Name contains query
    else if (nameLower.includes(queryLower)) {
      score += 50;
    }
    // Query contains name
    else if (queryLower.includes(nameLower)) {
      score += 30;
    }
    // Any partial match
    else {
      score += 10;
    }

    // Prefer more recent versions (rough heuristic)
    if (app.current_implementation_id.includes("@")) {
      const version = app.current_implementation_id.split("@")[1];
      if (version) {
        const versionNum = parseFloat(version);
        if (!isNaN(versionNum)) {
          score += Math.min(versionNum, 10); // Cap at 10 points
        }
      }
    }

    return {
      app,
      score,
    };
  });

  // Sort by score descending
  scored.sort((a, b) => b.score - a.score);
  const best = scored[0];

  return {
    app_name: best.app.name,
    implementation_id: best.app.current_implementation_id,
    confidence: Math.min(best.score / 100, 1), // Normalize to 0-1
  };
}

/**
 * Creates a cache key for implementation data
 */
export function createImplementationCacheKey(
  implementation_id: string,
): string {
  return `impl_${implementation_id.replace(/[^a-zA-Z0-9]/g, "_")}`;
}

/**
 * Validates that an implementation_id has the expected format
 */
export function isValidImplementationId(implementation_id: string): boolean {
  return /^[A-Za-z0-9]+@\d+\.\d+\.\d+$/.test(implementation_id);
}

/**
 * Extracts app name from implementation_id
 */
export function getAppNameFromImplementationId(
  implementation_id: string,
): string {
  return implementation_id.split("@")[0];
}

/**
 * Compares two implementation versions
 * Returns: -1 if a < b, 0 if a === b, 1 if a > b
 */
export function compareVersions(versionA: string, versionB: string): number {
  const parseVersion = (version: string) => version.split(".").map(Number);

  const a = parseVersion(versionA);
  const b = parseVersion(versionB);

  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const aVal = a[i] || 0;
    const bVal = b[i] || 0;

    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
  }

  return 0;
}
